import { isEmpty, sum, sumBy } from 'lodash';
import moment from 'moment-timezone';

import { createUrl, restGet } from '~/src/dash/api/rest';
import {
  IRealtimeAnalyticsPayload,
  IRealtimeSmartFloorMetricKey,
} from '~/src/dash/apiV2/smartFloors/common';

const MINUTES_PER_PERIOD = 10; // Default value for minutes per period for smart floors

export enum SmartFloorBidFloorType {
  Smart = 5, // Smart bid floor
  Control = 4, // Control bid floor
  Optimized = 3, // Optimized bid floor - V1 - not used in Thompson Sampling
  Low = 2, // Low bid floor - V1 - not used in Thompson Sampling
  High = 1, // High bid floor - V1 - not used in Thompson Sampling
}

export interface ISmartFloorRealtimeDataPerBidFloorAndPeriod {
  BidFloor: number;
  BidRequests: number;
  BidRequestPercentage: number; // BidRequests / TotalBidRequests * 100
  BidResponses: number;
  CPM: number;
  Date: moment.Moment;
  EstimatedRevenue: number;
  IsControlBidFloor: boolean;
  SmartFloorID: number;
  TotalBidRequests: number; // Total bid requests for the period
  WinningBids: number;
}

export type ISmartFloorRealtimeDataPerPeriod = Record<
  string, // bidFloor_bidFloorType
  ISmartFloorRealtimeDataPerBidFloorAndPeriod
>;

export type ISmartFloorRealtimeData = ISmartFloorRealtimeDataPerPeriod[];

/** getTimePeriodOfDay
 * calculate the time period of day based on the given date and minutes per period
 * period start at 1
 */
export function getTimePeriodOfDay(
  date: moment.Moment,
  minutesPerPeriod: number = MINUTES_PER_PERIOD,
): number {
  const totalMinutesInDay = 24 * 60;
  const periodsPerDay = Math.floor(totalMinutesInDay / minutesPerPeriod);

  const minutesFromStartOfDay = date.hours() * 60 + date.minutes();
  const period = Math.floor(minutesFromStartOfDay / minutesPerPeriod) + 1; // Add 1 to make periods 1-based

  return Math.min(period, periodsPerDay);
}

/** genSmartFloorDatesAndPeriodsKeys
 * generates a list of smart floor dates and periods keys in the format "YYYY-MM-DD_period"
 */
export function genSmartFloorDatesAndPeriodsKeys(
  startTime: moment.Moment,
  endTime: moment.Moment,
  minutesPerPeriod: number = MINUTES_PER_PERIOD,
): string[] {
  // truncate start time to the the start of the current period
  // ex: 2023-10-01 12:34:56 -> 2023-10-01 12:30:00
  let curr = startTime.clone(); // Clone the start time
  const currPeriod = getTimePeriodOfDay(curr, minutesPerPeriod);
  const minutesSinceStartofDay = (currPeriod - 1) * minutesPerPeriod; // Subtract 1 since periods is 1-based
  const currHour = Math.floor(minutesSinceStartofDay / 60);
  const currMin = minutesSinceStartofDay - currHour * 60;
  curr = curr.startOf('day').add(currHour, 'hours').add(currMin, 'minutes');

  // where startTime equals endTime - return 1 period
  if (startTime.isSame(endTime)) {
    const currPeriod = getTimePeriodOfDay(curr, minutesPerPeriod);
    const dateStr = curr.format('YYYY-MM-DD');
    return [`${dateStr}_${currPeriod}`];
  }

  // loop through the time periods until the end time, collect keys in this format "YYYY-MM-DD_period"
  const keys: string[] = [];
  while (curr.isBefore(endTime)) {
    const currPeriod = getTimePeriodOfDay(curr, minutesPerPeriod);
    const dateStr = curr.format('YYYY-MM-DD');
    keys.push(`${dateStr}_${currPeriod}`);
    curr = curr.add(minutesPerPeriod, 'minutes');
  }

  return keys;
}

/** calculateBidRequestPercentages
 * calculates bid request percentages for each bid floor, ensuring they sum to exactly 100%
 */
export function calculateBidRequestPercentages(
  periodData: Record<string, ISmartFloorRealtimeDataPerBidFloorAndPeriod>,
  totalBidRequests: number,
): { [key: string]: number } {
  const keys = Object.keys(periodData);

  let runningTotal = 0;
  const percentages: { [key: string]: number } = {};

  for (let i = 0; i < keys.length; i++) {
    // If there are no bid requests, return 0% for all bid floors
    if (totalBidRequests <= 0) {
      percentages[keys[i]] = 0.0;
      continue;
    }

    const bidFloor = keys[i];
    if (Object.prototype.hasOwnProperty.call(periodData, bidFloor)) {
      if (i === keys.length - 1) {
        // For the last item, assign the remaining percentage to ensure sum = 100.00
        const remainingPercentage = 100 - runningTotal;
        percentages[bidFloor] = parseFloat(remainingPercentage.toFixed(2));
      } else {
        // Calculate percentage with 2 decimal places
        const percentage =
          totalBidRequests > 0
            ? parseFloat(((periodData[bidFloor].BidRequests / totalBidRequests) * 100).toFixed(2))
            : 0.0;
        percentages[bidFloor] = percentage;
        runningTotal += percentage;
      }
    }
  }

  return percentages;
}

const constructBidFLoorKey = (
  bidFloor: string | number,
  bidFloorType: SmartFloorBidFloorType,
): string => {
  return `${bidFloor}_${bidFloorType}`;
};

export const parseBidFloorKey = (
  bidFloorKey: string,
): { bidFloor: number; bidFloorType: SmartFloorBidFloorType } => {
  const [bidFloorStr, bidFloorTypeStr] = bidFloorKey.split('_');
  return {
    bidFloor: Number(bidFloorStr),
    bidFloorType: Number(bidFloorTypeStr) as SmartFloorBidFloorType,
  };
};

/** processRealtimeSmartFloorItem
 * processes a single smart floor data entry and adds it to the aggregated data structure
 */
export function processRealtimeSmartFloorItem(
  key: string,
  value: { metrics: Record<IRealtimeSmartFloorMetricKey, { data: number[] }> },
  aggregatedData: Record<number, Record<string, ISmartFloorRealtimeDataPerBidFloorAndPeriod>>,
): void {
  // parse the key
  const [smartFloorID, date, period, bidFloorStr, bidFloorTypeStr] = key.split('_');

  // validate key
  if (!smartFloorID || !date || !period || !bidFloorStr || !bidFloorTypeStr) {
    console.warn(`Invalid key format for realtime: ${key}`);
    return;
  }

  // convert time into unix
  const dateKey: number = moment
    .tz(date, 'YYYY-MM-DD', 'UTC')
    .add((Number(period) - 1) * MINUTES_PER_PERIOD, 'minutes') // Subtract 1 since periods is 1-based
    .unix();

  const bidFloorType = Number(bidFloorTypeStr);
  // add the bidfloor data to correct time key in aggregatedData
  aggregatedData[dateKey] = {
    ...(aggregatedData[dateKey] || {}), // collect existing data
    [constructBidFLoorKey(bidFloorStr, bidFloorType)]: {
      BidFloor: Number(bidFloorStr),
      BidRequests: sum(value?.metrics?.bid_request?.data) || 0,
      BidRequestPercentage: 0, // Will be calculated later
      BidResponses: sum(value?.metrics?.bid_response?.data) || 0,
      CPM: sum(value?.metrics?.bid_won_cpm?.data) || 0,
      Date: moment.unix(dateKey),
      EstimatedRevenue: 0,
      IsControlBidFloor: bidFloorType === Number(SmartFloorBidFloorType.Control),
      SmartFloorID: smartFloorID ? Number(smartFloorID) : 0,
      TotalBidRequests: 0, // Will be calculated later
      WinningBids: sum(value?.metrics?.bid_won?.data) || 0,
    },
  };
}

/* aggregateBidderSmartFloorsRealtimeData
 * aggregate smart floor realtime data by time (date + period) and bid floor
 */
export function aggregateBidderSmartFloorsRealtimeData(
  data: IRealtimeAnalyticsPayload,
  isInventorySmartFloor: boolean,
): ISmartFloorRealtimeData {
  // validate data
  if (
    !data ||
    !data.dimensions ||
    isEmpty(
      isInventorySmartFloor
        ? data.dimensions.inventory_smart_floor_id?.items
        : data.dimensions.bidder_smart_floor_id?.items,
    )
  ) {
    return [];
  }

  const smartFloorData = isInventorySmartFloor
    ? data.dimensions.inventory_smart_floor_id?.items
    : data.dimensions.bidder_smart_floor_id?.items;

  // 1. Group realtime data by period and bid floor
  // key is unix timestamp, value is an map where keys are bidFloor_bidFloorType and values are those floors' data
  const aggregatedDataByPeriodAndBidFloor: Record<
    number, // unix timestamp
    Record<string, ISmartFloorRealtimeDataPerBidFloorAndPeriod> // { [bidFloor_bidFloorType]: value, ... }
  > = {};
  if (smartFloorData && typeof smartFloorData === 'object') {
    Object.entries(smartFloorData).forEach(([itemKey, itemValue]) => {
      processRealtimeSmartFloorItem(itemKey, itemValue, aggregatedDataByPeriodAndBidFloor);
    });
  }

  // 2. Loop through aggregated data and calculate percentages, revenue, and CPM of each bid floor
  const result: ISmartFloorRealtimeData = Object.values(aggregatedDataByPeriodAndBidFloor).map(
    (periodData) => {
      const totalBidRequests = sumBy(Object.values(periodData), 'BidRequests');

      // Calculate request percentages of each bid floor and ensure they sum to exactly 100
      const bidFloorToBidRequestPercentage = calculateBidRequestPercentages(
        periodData,
        totalBidRequests,
      );

      // Calculate revennue, cpm, and other metrics for each bid floor
      for (const key in periodData) {
        if (Object.prototype.hasOwnProperty.call(periodData, key)) {
          periodData[key].TotalBidRequests = totalBidRequests;
          periodData[key].BidRequestPercentage = bidFloorToBidRequestPercentage[key];
        }
      }

      // return the updated period data after processing
      return periodData;
    },
  );

  // 3. Sort the result by time asc, to help with futher aggregation and visualization in the UI
  return result.sort((a, b) => {
    const aDate = Object.values(a)[0].Date;
    const bDate = Object.values(b)[0].Date;
    return aDate.isBefore(bDate) ? -1 : aDate.isAfter(bDate) ? 1 : 0;
  });
}

/** getAdaptiveSmartFloorLiveData
 * fetches smart floor realtime data and aggregate them for Thompson Sampling graphs
 */
export async function getAdaptiveSmartFloorLiveData(
  publisherID: string,
  smartFloorID: number,
  startTime: moment.Moment,
  endTime: moment.Moment,
  isInventorySmartFloor: boolean,
): Promise<ISmartFloorRealtimeData> {
  const smartFloorIDParam = isInventorySmartFloor
    ? 'inventory_smart_floor_ids'
    : 'bidder_smart_floor_ids';
  const queryParams = new URLSearchParams({
    begin_time: startTime.unix().toString(),
    end_time: endTime.unix().toString(),
    [smartFloorIDParam]: String(smartFloorID),
    by_smart_floor_period: 'true',
    include_control_bid_floor: 'true',
    metrics: '3,9,12,75',
    smart_floor_dates_and_periods: genSmartFloorDatesAndPeriodsKeys(startTime, endTime, 10).join(
      ',',
    ),
  });

  const reqUrl = `${createUrl('/realtime_analytics', publisherID, 'v1')}&${queryParams.toString()}`;

  try {
    const response = await restGet<IRealtimeAnalyticsPayload>(reqUrl, {});
    return aggregateBidderSmartFloorsRealtimeData(
      response.parsedBody || ({} as IRealtimeAnalyticsPayload),
      isInventorySmartFloor,
    );
  } catch (e) {
    console.error(e);
    throw e;
  }
}
